import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronRight, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { env } from '@/config/env';
import { formatNumber, initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toApiDate as apiDate } from '@/lib/store-time';
import {
  useTimeSlotHostsReport,
  type HostEvent,
  type HostTimeSlot,
  type TimeSlotHostsModel,
} from './timeslot-hosts.api';

/**
 * Time slot hosts — who hosted what, over a period.
 *
 * The report answers one question two ways, so the grouping is a control rather
 * than two reports: by host, each person opens to the events they ran; by
 * event, each event-and-host pair opens to its time slots. The API returns both
 * groupings in one payload, so switching is free.
 *
 * One disclosure at every width. The page this replaces expanded a row in place
 * on a desktop and opened a full-screen drawer on a phone, which meant two
 * layouts of the same list that could — and did — carry different columns.
 */

const BY_HOST = 'host';
const BY_EVENT = 'event';

/** Rows per page inside an opened group. */
const DETAIL_PAGE_SIZE = 8;

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const lastOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
};

/** Wall-clock at the venue, so shift by the offset and format in UTC. */
function venueTime(value: string | null | undefined) {
  if (!value) return '—';
  const date = new Date(/[zZ]$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return '—';
  const shifted = new Date(date.getTime() + env.timezoneOffset * 3_600_000);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(shifted);
}

/** One line of the opened list — a slot, or an event with its own times. */
function DetailRow({
  row,
  eventName,
}: {
  row: HostTimeSlot;
  eventName?: string | null;
}) {
  const { t } = useTranslation();
  const full =
    (row.maxCapacity ?? 0) > 0 && (row.currentCapacity ?? 0) >= (row.maxCapacity ?? 0);

  return (
    <div className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-center sm:gap-6">
      {eventName !== undefined && (
        <p className="min-w-0 flex-1 text-sm break-words">{eventName}</p>
      )}
      <p
        className={cn(
          'text-muted-foreground min-w-0 text-xs sm:text-sm',
          eventName === undefined && 'flex-1'
        )}
      >
        {venueTime(row.startTime)} – {venueTime(row.endTime)}
      </p>
      <p className="text-sm tabular-nums sm:w-28 sm:text-right">
        <span className={cn(full && 'text-warning font-semibold')}>
          {formatNumber(row.currentCapacity)}
        </span>
        <span className="text-muted-foreground">
          /{formatNumber(row.maxCapacity)}
        </span>
        <span className="text-muted-foreground ml-1 text-xs">
          {t('timeslotHosts.booked')}
        </span>
      </p>
    </div>
  );
}

/** One host or one event, opening to the rows underneath it. */
function Group({
  title,
  subtitle,
  avatar,
  name,
  rows,
  withEventName,
}: {
  title: string;
  subtitle?: string | null;
  avatar?: string | null;
  name?: string | null;
  rows: HostTimeSlot[];
  /** True when the opened rows are events and carry their own name. */
  withEventName: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  const pageCount = Math.ceil(rows.length / DETAIL_PAGE_SIZE);
  const visible = rows.slice(
    page * DETAIL_PAGE_SIZE,
    (page + 1) * DETAIL_PAGE_SIZE
  );
  const canOpen = rows.length > 0;

  const header = (
    <div className="flex w-full items-center gap-3 p-3 text-left">
      {canOpen ? (
        <ChevronRight
          className={cn(
            'text-muted-foreground size-4 shrink-0 transition-transform',
            open && 'rotate-90'
          )}
        />
      ) : (
        <span className="size-4 shrink-0" />
      )}

      {avatar !== undefined && (
        <Avatar className="size-8 shrink-0">
          {avatar && <AvatarImage src={avatar} alt="" />}
          <AvatarFallback>{initials(name, null)}</AvatarFallback>
        </Avatar>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        {subtitle && (
          <p className="text-muted-foreground truncate text-xs">{subtitle}</p>
        )}
      </div>

      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold tabular-nums">
          {formatNumber(rows.length)}
        </p>
        <p className="text-muted-foreground text-xs">
          {withEventName ? t('timeslotHosts.events') : t('timeslotHosts.timeSlots')}
        </p>
      </div>
    </div>
  );

  if (!canOpen) return header;

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPage(0);
      }}
    >
      <CollapsibleTrigger className="hover:bg-accent/40 w-full transition-colors">
        {header}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="bg-muted/40 divide-y border-t">
          {visible.map((row, index) => (
            <DetailRow
              key={`${row.startTime ?? ''}-${index}`}
              row={row}
              eventName={
                withEventName ? (row as HostEvent).eventName : undefined
              }
            />
          ))}
          {pageCount > 1 && (
            <Pagination
              className="p-3"
              page={page}
              size={DETAIL_PAGE_SIZE}
              total={rows.length}
              onPageChange={setPage}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function TimeSlotHostsPage() {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(lastOfMonth);
  const [groupBy, setGroupBy] = useState(BY_HOST);

  const model: TimeSlotHostsModel = useMemo(
    () => ({
      startDate: apiDate(start, false),
      endDate: apiDate(end, true),
      query,
    }),
    [start, end, query]
  );

  const report = useTimeSlotHostsReport(model);
  const byEvent = groupBy === BY_EVENT;

  const hosts = report.data?.hosts ?? [];
  const events = report.data?.events ?? [];
  const groups = byEvent ? events : hosts;

  const filterFields: FilterField[] = [
    {
      id: 'start',
      label: t('timeslotHosts.from'),
      value: start,
      onChange: setStart,
      type: 'date',
      resetValue: '',
    },
    {
      id: 'end',
      label: t('timeslotHosts.to'),
      value: end,
      onChange: setEnd,
      type: 'date',
      resetValue: '',
    },
    {
      // The original had this as a switch beside the filters. As a field it
      // rides into the mobile sheet with everything else, and says what both
      // of its states are rather than only the one it is not in.
      id: 'groupBy',
      label: t('timeslotHosts.groupBy'),
      value: groupBy,
      onChange: setGroupBy,
      sort: false,
      resetValue: BY_HOST,
      options: [
        { value: BY_HOST, label: t('timeslotHosts.byHost') },
        { value: BY_EVENT, label: t('timeslotHosts.byEvent') },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('timeslotHosts.title')}
        description={t('timeslotHosts.subtitle')}
      />

      <FilterBar
        busy={report.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query) void report.refetch();
              setQuery(draft);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('timeslotHosts.searchPlaceholder')}
                aria-label={t('timeslotHosts.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          report.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {byEvent
                ? t('timeslotHosts.nEvents', { count: groups.length })
                : t('timeslotHosts.nHosts', { count: groups.length })}
            </p>
          ) : null
        }
      />

      {report.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('timeslotHosts.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void report.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {report.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-8 shrink-0 rounded-full" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      )}

      {report.isSuccess && groups.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={report.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('timeslotHosts.noResults')}
          </p>
        </div>
      )}

      {groups.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={report.isFetching} />
          <div
            className={cn(
              'divide-y overflow-hidden rounded-xl border',
              report.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {byEvent
              ? events.map((event, index) => (
                  <Group
                    // Grouped by event the grid has one row per event *per host*,
                    // so the name alone repeats; neither field carries an id, and
                    // the list has no paging or client sort, so position is a
                    // safe final tiebreaker.
                    key={`${event.eventName ?? ''}|${event.completeName ?? ''}|${index}`}
                    title={event.eventName ?? '—'}
                    subtitle={event.completeName}
                    avatar={event.avatar}
                    name={event.completeName}
                    rows={event.timeSlots ?? []}
                    withEventName={false}
                  />
                ))
              : hosts.map((host, index) => (
                  <Group
                    key={`${host.completeName ?? ''}|${index}`}
                    title={host.completeName ?? '—'}
                    avatar={host.avatar}
                    name={host.completeName}
                    rows={host.events ?? []}
                    withEventName
                  />
                ))}
          </div>
        </div>
      )}
    </div>
  );
}
